(function () {
  var STORAGE_KEY = 'nfd_size_profile_v1';
  var PROFILE_ENDPOINT = '/apps/size-sync/profile';

  if (!window.nfdCustomer || window.nfdCustomer.loggedIn !== true) {
    return;
  }

  function readLocalProfile() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;

      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeLocalProfile(profile) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
      return true;
    } catch (e) {
      return false;
    }
  }

  function normalizeBottoms(data) {
    data = data || {};
    return {
      waist: data.waist ?? null,
      upperThigh: data.upperThigh ?? null,
      frontRise: data.frontRise ?? null,
      backRise: data.backRise ?? null,
      knee: data.knee ?? null,
      legOpening: data.legOpening ?? null,
      inseam: data.inseam ?? null,
      updatedAt: data.updatedAt ?? null
    };
  }

  function normalizeTops(data) {
    data = data || {};
    return {
      shoulder: data.shoulder ?? null,
      pitToPit: data.pitToPit ?? null,
      sleeveLength: data.sleeveLength ?? null,
      bodyLength: data.bodyLength ?? null,
      updatedAt: data.updatedAt ?? null
    };
  }

  function hasAnyValues(obj, keys) {
    if (!obj || typeof obj !== 'object') return false;

    return keys.some(function (key) {
      return obj[key] != null && obj[key] !== '';
    });
  }

  function countFilledValues(obj, keys) {
    if (!obj || typeof obj !== 'object') return 0;

    return keys.reduce(function (count, key) {
      return count + ((obj[key] != null && obj[key] !== '') ? 1 : 0);
    }, 0);
  }

  function getProfileCompleteness(profile) {
    if (!profile || typeof profile !== 'object') return 0;

    var bottomsKeys = ['waist', 'upperThigh', 'frontRise', 'backRise', 'knee', 'legOpening', 'inseam'];
    var topsKeys = ['shoulder', 'pitToPit', 'sleeveLength', 'bodyLength'];

    return countFilledValues(profile.bottoms, bottomsKeys) + countFilledValues(profile.tops, topsKeys);
  }

  function normalizeLocalProfileShape(profile) {
    if (!profile || typeof profile !== 'object') return null;

    var bottoms = null;
    var tops = null;

    if (profile.bottoms && typeof profile.bottoms === 'object') {
      bottoms = normalizeBottoms(profile.bottoms);
    } else if (profile.jeans && typeof profile.jeans === 'object') {
      bottoms = normalizeBottoms(profile.jeans);
    } else if (profile.garment && typeof profile.garment === 'object') {
      if (hasAnyValues(profile.garment, ['waist', 'upperThigh', 'frontRise', 'backRise', 'knee', 'legOpening', 'inseam'])) {
        bottoms = normalizeBottoms(profile.garment);
      }

      if (hasAnyValues(profile.garment, ['shoulder', 'pitToPit', 'sleeveLength', 'bodyLength'])) {
        tops = normalizeTops(profile.garment);
      }
    }

    if (!tops && profile.tops && typeof profile.tops === 'object') {
      tops = normalizeTops(profile.tops);
    }

    return {
      version: profile.version ?? 2,
      unit: 'in',
      bottoms: bottoms,
      tops: tops,
      updatedAt: profile.updatedAt ?? null
    };
  }

  function backendToLocalProfile(data) {
    if (!data || typeof data !== 'object') {
      return null;
    }

    var bottoms = null;
    var tops = null;

    if (data.bottoms && typeof data.bottoms === 'object') {
      bottoms = normalizeBottoms(data.bottoms);
    }

    if (data.tops && typeof data.tops === 'object') {
      tops = normalizeTops(data.tops);
    }

    if (
      !bottoms &&
      data.measurements &&
      typeof data.measurements === 'object' &&
      hasAnyValues(data.measurements, ['waist', 'upperThigh', 'frontRise', 'backRise', 'knee', 'legOpening', 'inseam'])
    ) {
      bottoms = normalizeBottoms(data.measurements);
    }

    if (
      !tops &&
      data.measurements &&
      typeof data.measurements === 'object' &&
      hasAnyValues(data.measurements, ['shoulder', 'pitToPit', 'sleeveLength', 'bodyLength'])
    ) {
      tops = normalizeTops(data.measurements);
    }

    return {
      version: data.version ?? 2,
      unit: 'in',
      bottoms: bottoms,
      tops: tops,
      updatedAt: data.updatedAt ?? null
    };
  }

  function localToBackendProfile(profile) {
    profile = normalizeLocalProfileShape(profile);
    if (!profile) return null;

    return {
      bottoms: profile.bottoms || null,
      tops: profile.tops || null,
      version: profile.version ?? 2,
      updatedAt: profile.updatedAt ?? new Date().toISOString()
    };
  }

  function fetchRemoteProfile() {
    return fetch(PROFILE_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin'
    }).then(function (res) {
      if (!res.ok) throw new Error('Profile fetch failed');
      return res.json();
    });
  }

  function pushRemoteProfile(profile) {
    var payload = localToBackendProfile(profile);
    if (!payload) return Promise.resolve(false);

    return fetch(PROFILE_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error('Profile save failed');
      return res.json();
    }).then(function (data) {
      return !!(data && data.success);
    });
  }

  function getProfileTimestamp(profile) {
    if (!profile || !profile.updatedAt) return 0;

    var ts = Date.parse(profile.updatedAt);
    return isNaN(ts) ? 0 : ts;
  }

  function profilesEqual(a, b) {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch (e) {
      return false;
    }
  }

  function resolveProfiles(localProfile, remoteProfile) {
    if (!localProfile && !remoteProfile) {
      return { action: 'none' };
    }

    if (localProfile && !remoteProfile) {
      return {
        action: 'push-local',
        profile: localProfile
      };
    }

    if (!localProfile && remoteProfile) {
      return {
        action: 'write-remote',
        profile: remoteProfile
      };
    }

    var localTs = getProfileTimestamp(localProfile);
    var remoteTs = getProfileTimestamp(remoteProfile);

    if (localTs > remoteTs) {
      return {
        action: 'push-local',
        profile: localProfile
      };
    }

    if (remoteTs > localTs) {
      return {
        action: 'write-remote',
        profile: remoteProfile
      };
    }

    if (!profilesEqual(localProfile, remoteProfile)) {
      var localScore = getProfileCompleteness(localProfile);
      var remoteScore = getProfileCompleteness(remoteProfile);

      if (localScore > remoteScore) {
        return {
          action: 'push-local',
          profile: localProfile
        };
      }

      if (remoteScore > localScore) {
        return {
          action: 'write-remote',
          profile: remoteProfile
        };
      }

      return {
        action: 'push-local',
        profile: localProfile
      };
    }

    return { action: 'none' };
  }

  function syncProfiles() {
    var localProfile = normalizeLocalProfileShape(readLocalProfile());

    fetchRemoteProfile()
      .then(function (data) {
        var remoteProfile = backendToLocalProfile(data);
        var decision = resolveProfiles(localProfile, remoteProfile);

        if (decision.action === 'push-local' && decision.profile) {
          return pushRemoteProfile(decision.profile);
        }

        if (decision.action === 'write-remote' && decision.profile) {
          writeLocalProfile(decision.profile);
          document.dispatchEvent(new CustomEvent('nfd:size-profile-loaded', {
            detail: decision.profile
          }));
        }

        return null;
      })
      .catch(function () {});
  }

  syncProfiles();

  document.addEventListener('nfd:size-profile-save', function (event) {
    var profile = normalizeLocalProfileShape(event.detail);
    if (!profile) return;

    profile.updatedAt = new Date().toISOString();

    writeLocalProfile(profile);
    pushRemoteProfile(profile).catch(function () {});
  });
})();